import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Spinner } from 'react-bootstrap';
import {
  ArrowClockwise,
  ClockHistory,
  Download,
  Upload,
} from 'react-bootstrap-icons';

import AuditoriaDespacho from '../../components/partidas/AuditoriaDespacho';
import HistorialImportaciones from '../../components/partidas/HistorialImportaciones';
import PartidaDetalleModal from '../../components/partidas/PartidaDetalleModal';
import PartidasFiltros from '../../components/partidas/PartidasFiltros';
import PartidasMetricas from '../../components/partidas/PartidasMetricas';
import PartidasTable from '../../components/partidas/PartidasTable';
import {
  createDefaultPartidasFilters,
  ESTADOS_VENCIMIENTO,
  PARTIDAS_PAGE_SIZE,
} from '../../constants/partidas';
import { formatDateTime, formatNumber } from '../../utils/partidasFormatters';
import { getAllPartidasForExport } from './partidasService';
import usePartidas from './usePartidas';
import ActiveFiltersLegend from '../../components/ActiveFiltersLegend';

const ImportacionPartidasModal = lazy(() =>
  import('../../components/partidas/ImportacionPartidasModal')
);

export default function PartidasPage() {
  const [filters, setFilters] = useState(createDefaultPartidasFilters);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('fecha_vencimiento');
  const [sortAscending, setSortAscending] = useState(true);
  const [activeView, setActiveView] = useState('vencimientos');
  const [selected, setSelected] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [moduleRefreshKey, setModuleRefreshKey] = useState(0);

  const {
    rows,
    total,
    summary,
    options,
    lastImport,
    loading,
    error,
    dataError,
    refresh,
  } = usePartidas({
    filters,
    page,
    pageSize: PARTIDAS_PAGE_SIZE,
    sortField,
    sortAscending,
  });

  useEffect(() => {
    if (!loading && !dataError) {
      const lastPage = Math.max(1, Math.ceil(total / PARTIDAS_PAGE_SIZE));
      if (page > lastPage) setPage(lastPage);
    }
  }, [dataError, loading, page, total]);

  const resultLabel = useMemo(
    () => `${formatNumber(total)} partida${total === 1 ? '' : 's'}`,
    [total]
  );

  const activeFilterItems = useMemo(() => {
    const estadoLabels = new Map(
      ESTADOS_VENCIMIENTO.map(({ value, label }) => [value, label])
    );

    return [
      filters.busqueda && { label: 'Buscar', value: filters.busqueda },
      filters.productos?.length > 0 && { label: 'Productos', value: filters.productos },
      filters.niveles?.length > 0 && { label: 'Líneas', value: filters.niveles },
      filters.proveedores?.length > 0 && { label: 'Proveedores', value: filters.proveedores },
      filters.estados?.length > 0 && {
        label: 'Estados',
        value: filters.estados.map((estado) => estadoLabels.get(estado) || estado),
      },
      filters.fechaDesde && { label: 'Desde', value: filters.fechaDesde },
      filters.fechaHasta && { label: 'Hasta', value: filters.fechaHasta },
    ].filter(Boolean);
  }, [filters]);

  const handleApplyFilters = (nextFilters) => {
    setFilters({ ...nextFilters });
    setPage(1);
  };

  const handleClearFilters = () => {
    const cleared = createDefaultPartidasFilters();
    setFilters(cleared);
    setPage(1);
    return cleared;
  };

  const handleSort = (field) => {
    setPage(1);
    if (field === sortField) {
      setSortAscending((ascending) => !ascending);
    } else {
      setSortField(field);
      setSortAscending(true);
    }
  };

  const handleRefresh = () => {
    refresh();
    setModuleRefreshKey((key) => key + 1);
  };

  const handleExport = async () => {
    setExporting(true);
    setActionError('');
    try {
      const allRows = await getAllPartidasForExport(filters, { sortField, sortAscending });
      const { exportPartidasExcel } = await import('../../utils/partidasExport');
      await exportPartidasExcel({ rows: allRows, filters });
    } catch (exportError) {
      setActionError(exportError.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Container fluid as='main' className='partidas-wrapper'>
      <header className='crm-page-header partidas-page-header'>
        <div className='crm-page-heading'>
          <h1 className='crm-page-title'>Vencimiento de Partidas</h1>
          <p className='crm-page-subtitle'>
            Consultá vencimientos, existencias y la auditoría del proceso de despacho.
          </p>
          <span className='partidas-last-update'>
            Última actualización:{' '}
            {lastImport
              ? `${formatDateTime(lastImport.finalizado_at)} · ${lastImport.archivo_nombre}`
              : 'sin importaciones registradas'}
          </span>
        </div>

        <div className='crm-page-actions partidas-header-actions'>
          <Button variant='outline-primary' onClick={handleRefresh} title='Actualizar datos'>
            <ArrowClockwise /> Actualizar
          </Button>
          <Button variant='outline-primary' onClick={() => setShowHistory(true)}>
            <ClockHistory /> Historial
          </Button>
          <Button variant='primary' onClick={() => setShowImport(true)}>
            <Upload /> Importar / Actualizar
          </Button>
        </div>
      </header>

      <PartidasFiltros
        filters={filters}
        options={options}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      <ActiveFiltersLegend items={activeFilterItems} />

      <nav className='crm-subtabs partidas-view-tabs' aria-label='Secciones de Vencimiento de Partidas'>
        <button
          type='button'
          className={activeView === 'vencimientos' ? 'is-active' : ''}
          onClick={() => setActiveView('vencimientos')}
        >
          Vencimientos
        </button>
        <button
          type='button'
          className={activeView === 'auditoria' ? 'is-active' : ''}
          onClick={() => setActiveView('auditoria')}
        >
          Auditoría de Proceso de Despacho
        </button>
      </nav>

      {error && <Alert variant='danger' role='alert'>{error}</Alert>}
      {actionError && (
        <Alert variant='danger' role='alert' dismissible onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}

      {activeView === 'vencimientos' ? (
        <>
          <PartidasMetricas summary={summary} />

          <section className='partidas-results-card'>
            <div className='crm-results-header partidas-results-header'>
              <div>
                <span>Resultados del filtrado</span>
                <h2>Partidas encontradas</h2>
                <p>{resultLabel} con los filtros activos.</p>
              </div>
              <Button variant='outline-primary' disabled={!total || exporting} onClick={handleExport}>
                {exporting ? <Spinner animation='border' size='sm' /> : <Download />}
                {exporting ? ' Preparando...' : ' Exportar Excel'}
              </Button>
            </div>

            {loading ? (
              <div className='partidas-loading-state'>
                <Spinner animation='border' variant='primary' />
                <span>Cargando partidas...</span>
              </div>
            ) : dataError ? (
              <div className='partidas-empty-state' role='status'>
                No se pudieron consultar las partidas.
              </div>
            ) : rows.length === 0 ? (
              <div className='partidas-empty-state'>
                No se encontraron registros con los filtros seleccionados.
              </div>
            ) : (
              <PartidasTable
                rows={rows}
                total={total}
                page={page}
                pageSize={PARTIDAS_PAGE_SIZE}
                onPageChange={setPage}
                sortField={sortField}
                sortAscending={sortAscending}
                onSort={handleSort}
                onView={setSelected}
              />
            )}
          </section>
        </>
      ) : (
        <AuditoriaDespacho
          filters={filters}
          refreshKey={moduleRefreshKey}
          onView={setSelected}
        />
      )}

      <PartidaDetalleModal partida={selected} onHide={() => setSelected(null)} />
      {showImport && (
        <Suspense fallback={null}>
          <ImportacionPartidasModal
            show={showImport}
            onHide={() => setShowImport(false)}
            onImported={handleRefresh}
          />
        </Suspense>
      )}
      <HistorialImportaciones
        show={showHistory}
        onHide={() => setShowHistory(false)}
        refreshKey={moduleRefreshKey}
      />
    </Container>
  );
}
